import { ContentCopy as ContentCopyIcon, Download as DownloadIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Accordion, AccordionDetails, AccordionSummary, Alert, AppBar, Backdrop, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, Divider, Grid, IconButton, Link, List, ListItem, ListItemIcon, ListItemText, MenuItem, Paper, Select, Toolbar, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar, useUserDetailsStore } from '@zeta-core';
import { ComplianceService, getErrorMessage, ISessionsChatResponse, WPDocumentQA } from '@zeta-services';
import { IGenericErrorResponse } from '@zeta-types';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';

// ---------- Types (updated to match new response) ----------
type LibraryDocument = {
  id: string;            // server id
  fileName: string;
  uploadedAt: Date;
};

export type ExampleItem = { quote: string; location_hint?: string };
export type FindingFeedback = { client: string; advisor: string; examples: ExampleItem[] };
export type Finding = {
  section: string;
  status: 'Pass' | 'Fail' | 'Partial';
  compliance_percentage: string; // e.g., "100%"
  fca_reference: string[]; // now array
  issues: string[];
  feedback: FindingFeedback;
  remediation: string[];
};
export type ExtractedEntities = {
  clients?: string[];
  advisers_or_firm?: string[];
  wrappers_accounts?: string[];
  providers_custodians?: string[];
  products_securities?: string[];
  risk_profiles?: Record<string, { score: number; group: number; strategy: string }>;
  amounts_fees?: Record<string, string>;
};
export type ComplianceResponse = {
  document_type: string;
  recognised: boolean;
  summary: string;
  total_compliance_percentage: string; // e.g., "79%"
  findings: Finding[];
  extracted_entities?: ExtractedEntities;
  citations: string[];
};

const FilterChip: React.FC<{
  label: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, active, onClick }) => (
  <Chip
    label={label}
    onClick={onClick}
    color={active ? 'primary' : 'default'}
    variant={active ? 'filled' : 'outlined'}
    size="small"
    sx={{
      height: 32,
      fontSize: '0.75rem',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: active ? 'primary.dark' : 'action.hover',
      },
    }}
  />
);

const joinList = (arr?: string[], max = 4) => {
  if (!arr || arr.length === 0) return '—';
  const head = arr.slice(0, max).join(', ');
  const extra = arr.length > max ? ` +${arr.length - max} more` : '';
  return head + extra;
};
// ---------- UI helpers ----------
const StatusBadge: React.FC<{ status: Finding['status'] }> = ({ status }) => {
  const getColor = (status: Finding['status']) => {
    switch (status) {
      case 'Pass':
        return 'success' as const;
      case 'Fail':
        return 'error' as const;
      case 'Partial':
        return 'warning' as const;
      default:
        return 'default' as const;
    }
  };
  return (
    <Chip
      label={
        status === 'Pass'
          ? 'Aligned'
          : status === 'Fail'
          ? 'Gaps identified'
          : 'Partially aligned'
      }
      color={getColor(status)}
      size="small"
      variant="outlined"
      sx={{
        fontSize: '0.75rem',
        height: '20px',
        '& .MuiChip-label': {
          px: 1,
        },
      }}
    />
  );
};

function toNumberPercent(pct: string) {
  const n = parseFloat(pct.replace('%', ''));
  return isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
}

// Animated Overall Donut (subtle)
const Donut: React.FC<{ value: number; size?: number; stroke?: number; label?: string }> = ({ value, size = 160, stroke = 14, label }) => {
  const theme = useTheme();
  const [anim, setAnim] = useState(0);

  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const dur = 800;
    const tick = (t: number) => {
      const p = Math.min((t - start) / dur, 1);
      setAnim(value * p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (anim / 100) * circ;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} stroke={theme.palette.grey[300]} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={theme.palette.primary.main}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box sx={{ position: 'absolute', textAlign: 'center' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1 }}>
          {Math.round(anim)}%
        </Typography>
        {label && (
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Linear progress bar (animated)
const LinearProgressBar: React.FC<{ value: number; status: Finding['status'] }> = ({ value, status }) => {
  const theme = useTheme();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setProgress(value), 100);
    return () => clearTimeout(timer);
  }, [value]);

  const getColor = (status: Finding['status']) => {
    switch (status) {
      case 'Pass':
        return theme.palette.success.main;
      case 'Fail':
        return theme.palette.error.main;
      case 'Partial':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Compliance
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }} color="text.primary">
          {Math.round(progress)}%
        </Typography>
      </Box>
      <Box
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: 'grey.200',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${progress}%`,
            bgcolor: getColor(status),
            transition: 'width 600ms ease-out',
            borderRadius: 5,
          }}
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </Box>
    </Box>
  );
};

// Waiting overlay (full-page)
const WaitScreen: React.FC<{ fileName?: string }> = ({ fileName }) => (
  <Backdrop
    sx={{
      color: 'text.primary',
      zIndex: (theme) => theme.zIndex.drawer + 1,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(4px)',
    }}
    open={true}
  >
    <Box sx={{ maxWidth: 400, width: '100%', textAlign: 'center', p: 3 }}>
      <CircularProgress
        size={64}
        sx={{
          color: 'primary.main',
          mb: 2,
        }}
      />
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        Analysing your PDF…
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: fileName ? 1 : 0 }}>
        Extracting content and reviewing it against the selected compliance topic and reference materials.
      </Typography>
      {fileName && (
        <Typography variant="caption" color="text.secondary">
          File:{' '}
          <Typography component="span" sx={{ fontWeight: 500 }}>
            {fileName}
          </Typography>
        </Typography>
      )}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              animation: 'bounce 1.4s ease-in-out infinite both',
              animationDelay: `${i * 150}ms`,
              '@keyframes bounce': {
                '0%, 80%, 100%': {
                  transform: 'scale(0)',
                },
                '40%': {
                  transform: 'scale(1)',
                },
              },
            }}
          />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Contacting compliance service
      </Typography>
    </Box>
  </Backdrop>
);

const ComplianceLibraryPanel: React.FC<{
  documents: LibraryDocument[];
  onUpload: (files: FileList | null) => void;
  onRemove: (id: string) => void;
}> = ({ documents, onUpload, onRemove }) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  return (
    <Box sx={{ position: 'sticky', top: 96 }}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LibraryBooksIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Compliance reference library
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Documents used as reference material to support compliance feedback.
          </Typography>

          {/* Upload */}
          <Box sx={{ mt: 2 }}>
            <Button
              component="label"
              fullWidth
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ borderRadius: 2 }}
            >
              Add documents

              <input
                ref={inputRef}
                type="file"
                accept="application/pdf"
                multiple
                hidden
                onChange={(e) => {
                  onUpload(e.target.files);

                  // ✅ CRITICAL: reset input so same file can be reselected
                  if (inputRef.current) {
                    inputRef.current.value = '';
                  }
                }}
              />
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* List */}
          {documents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No compliance documents uploaded.
            </Typography>
          ) : (
            <List dense sx={{ p: 0 }}>
              {documents.map((doc) => (
                <ListItem
                  key={doc.id}
                  sx={{ px: 0 }}
                  secondaryAction={
                    <Button
                      size="small"
                      color="error"
                      onClick={() => onRemove(doc.id)}
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Remove
                    </Button>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LibraryBooksIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {doc.fileName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Added {doc.uploadedAt.toLocaleDateString()}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="caption">
              Documents in this library are used as reference material to support
              compliance feedback. They do not constitute automated approval
              or regulatory validation.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

const ResultsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  data: ComplianceResponse;
  file: File | null;
  counts: any;
  filteredFindings: Finding[];
  expandAll: boolean;
  setExpandAll: React.Dispatch<React.SetStateAction<boolean>>;
  filter: any;
  setFilter: any;
  libraryDocs: LibraryDocument[];
  selectedTopicName: string;
}> = ({
  open,
  onClose,
  data,
  file,
  counts,
  filteredFindings,
  expandAll,
  setExpandAll,
  filter,
  setFilter,
  libraryDocs,
  selectedTopicName,
}) => {
  return (
    <Dialog fullScreen open={open} onClose={onClose}>

      {/* Top App Bar */}
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6">
            Compliance Review Summary
          </Typography>
          <IconButton edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* RESULTS CONTENT — unchanged */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* LEFT COLUMN */}
          <Grid size={{ xs: 12, lg: 3}}>
            {/* ⬇️ paste your entire LEFT scorecard stack here */}
                  <Card sx={{ borderRadius: 3}}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Review context
                      </Typography>

                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Recommendation document
                        </Typography>
                        <Typography variant="body2">
                          {file?.name || '—'}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Reference materials in scope
                        </Typography>
                        <Typography variant="body2">

                        </Typography>
                      </Box>
                    </CardContent>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                          Compliance indicator
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Selected compliance topic
                        </Typography>
                      </Box>
                      {/* Meta chips */}
                      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Chip
                          label={data.document_type || 'Document'}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            bgcolor: 'grey.50',
                            color: 'text.primary',
                          }}
                        />
                        <Chip
                          label={data.recognised ? 'Recognised' : 'Not recognised'}
                          size="small"
                          variant="outlined"
                          color={data.recognised ? 'success' : 'error'}
                          sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                          }}
                        />
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', py: 3 }}>
                        <Donut value={toNumberPercent(data.total_compliance_percentage)} label="Compliance" />
                      </Box>
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        {data.summary}
                      </Typography>

                      {/* Key details extracted */}
                      {data.extracted_entities && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Key details
                          </Typography>
                          <Box sx={{ mt: 1, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                            {data.extracted_entities.clients && (
                              <Box>
                                <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                  Clients:{' '}
                                </Typography>
                                <Typography component="span" variant="caption">
                                  {joinList(data.extracted_entities.clients, 3)}
                                </Typography>
                              </Box>
                            )}
                            {data.extracted_entities.advisers_or_firm && (
                              <Box>
                                <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                  Firm:{' '}
                                </Typography>
                                <Typography component="span" variant="caption">
                                  {joinList(data.extracted_entities.advisers_or_firm, 2)}
                                </Typography>
                              </Box>
                            )}
                            {data.extracted_entities.wrappers_accounts && (
                              <Box>
                                <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                  Wrappers:{' '}
                                </Typography>
                                <Typography component="span" variant="caption">
                                  {joinList(data.extracted_entities.wrappers_accounts, 3)}
                                </Typography>
                              </Box>
                            )}
                            {data.extracted_entities.providers_custodians && (
                              <Box>
                                <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                  Providers:{' '}
                                </Typography>
                                <Typography component="span" variant="caption">
                                  {joinList(data.extracted_entities.providers_custodians, 3)}
                                </Typography>
                              </Box>
                            )}
                            {data.extracted_entities.products_securities && (
                              <Box>
                                <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                  Products:{' '}
                                </Typography>
                                <Typography component="span" variant="caption">
                                  {joinList(data.extracted_entities.products_securities, 4)}
                                </Typography>
                              </Box>
                            )}
                            {data.extracted_entities.amounts_fees && (
                              <Box>
                                <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                  Fees:{' '}
                                </Typography>
                                <Typography component="span" variant="caption">
                                  {Object.entries(data.extracted_entities.amounts_fees)
                                    .slice(0, 3)
                                    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
                                    .join('; ')}{' '}
                                  {Object.keys(data.extracted_entities.amounts_fees).length > 3 ? '…' : ''}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        Quick stats
                      </Typography>
                      <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, textAlign: 'center' }}>
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50', p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Fail
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {counts.fail}
                          </Typography>
                        </Box>
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50', p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Partial
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {counts.partial}
                          </Typography>
                        </Box>
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50', p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Pass
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {counts.pass}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>

                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1rem', mb: 1 }}>
                        Citations
                      </Typography>
                      <List dense sx={{ p: 0 }}>
                        {data.citations.map((c, i) => (
                          <ListItem key={i} sx={{ p: 0, mb: 0.5 }}>
                            <Link
                              href={c}
                              target="_blank"
                              rel="noreferrer"
                              variant="caption"
                              color="primary"
                              sx={{
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {c}
                            </Link>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
          </Grid>

          {/* FINDINGS */}
          <Grid size={{ xs: 12, lg: 6 }}>
            {/* ⬇️ paste your Findings card here */}
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                      <Box>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                          Findings
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Observations based on the selected compliance topic and reference materials
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterChip label="All" active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
                        <FilterChip label="Fail" active={filter === 'FAIL'} onClick={() => setFilter('FAIL')} />
                        <FilterChip label="Partial" active={filter === 'PARTIAL'} onClick={() => setFilter('PARTIAL')} />
                        <FilterChip label="Pass" active={filter === 'PASS'} onClick={() => setFilter('PASS')} />
                        <Divider orientation="vertical" flexItem sx={{ height: 24, mx: 0.5 }} />
                        <FilterChip label={expandAll ? 'Collapse all' : 'Expand all'} active={expandAll} onClick={() => setExpandAll((v) => !v)} />
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      {filteredFindings.map((f, idx) => {
                        const fcaRef = Array.isArray(f.fca_reference) ? f.fca_reference.join(', ') : (f.fca_reference ?? '');
                        return (
                          <Accordion
                            key={`${f.section}-${idx}`}
                            defaultExpanded={expandAll || f.status !== 'Pass'}
                            disableGutters
                            elevation={0}
                            sx={{
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 2,
                              mb: 1,
                              '&:before': {
                                display: 'none',
                              },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                px: 2,
                                py: 1.5,
                                minHeight: 'auto',
                                '& .MuiAccordionSummary-content': {
                                  alignItems: 'flex-start',
                                  mr: 2,
                                },
                              }}
                            >
                              <Box sx={{ pr: 2, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                                    {f.section}
                                  </Typography>
                                  <StatusBadge status={f.status} />
                                  <Typography variant="caption" color="text.secondary">
                                    {f.compliance_percentage}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  FCA Ref: {fcaRef}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2, pb: 2 }}>
                              <LinearProgressBar value={toNumberPercent(f.compliance_percentage)} status={f.status} />

                              {/* Issues */}
                              {f.issues && f.issues.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Issues
                                  </Typography>
                                  <List dense sx={{ p: 0, mt: 0.5 }}>
                                    {f.issues.map((issue, i) => (
                                      <ListItem key={i} sx={{ p: 0, alignItems: 'flex-start' }}>
                                        <Typography variant="body2" sx={{ pl: 2 }}>
                                          {issue}
                                        </Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}

                              {/* Feedback */}
                              <Box sx={{ mt: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Client feedback
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {f.feedback.client}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Advisor feedback
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {f.feedback.advisor}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Examples */}
                              {f.feedback.examples && f.feedback.examples.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Examples
                                  </Typography>
                                  <List dense sx={{ p: 0, mt: 0.5 }}>
                                    {f.feedback.examples.map((ex, i) => (
                                      <ListItem key={i} sx={{ p: 0, alignItems: 'flex-start' }}>
                                        <Box sx={{ pl: 2 }}>
                                          <Typography variant="body2" sx={{ fontStyle: 'italic', borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
                                            "{ex.quote}"
                                          </Typography>
                                          {ex.location_hint && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                              Location: {ex.location_hint}
                                            </Typography>
                                          )}
                                        </Box>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}

                              {/* Remediation */}
                              {f.remediation && f.remediation.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Remediation
                                  </Typography>
                                  <List dense sx={{ p: 0, mt: 0.5 }}>
                                    {f.remediation.map((r, i) => (
                                      <ListItem key={i} sx={{ p: 0, alignItems: 'flex-start' }}>
                                        <Typography variant="body2" sx={{ pl: 2 }}>
                                          {r}
                                        </Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>

                    {filteredFindings.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No findings match this filter.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};


// ---------- Page ----------
export function ConsumerDuty() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ComplianceResponse | null>(null);
  const [expandAll, setExpandAll] = useState(false);
  const [filter, setFilter] = useState<'ALL' | 'FAIL' | 'PARTIAL' | 'PASS'>('ALL');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const [libraryDocs, setLibraryDocs] = useState<LibraryDocument[]>([]);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [sessions, setSessions] = useState<ISessionsChatResponse[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string>('');
  const [selectedTopicName, setSelectedTopicName] = useState<string>('');

  const { id, accessibleEntityID } = useUserDetailsStore((userState) => userState.user);

  // =========================================================
  //  Compliance topics (derived from existing document review sessions)
  // =========================================================
  const getSessions = useMutation({
    mutationFn: () =>
      WPDocumentQA().chatSessionsByAdvisor({ advisor_id: id }), // replace if needed
    onSuccess: (response: AxiosResponse<ISessionsChatResponse[]>) => {
      setSessions(response.data || []);
    },
  });

const complianceTopics: ComplianceTopic[] = useMemo(() => {
  return sessions
    .filter(
      (s) =>
        s.chat_type === 'document_analysis' &&
        s.session_name &&
        s.session_name.toLowerCase() !== 'document assistant'
    )
    .map((s) => ({
      id: s.session_id,                 // ✅ backend identifier
      name: s.session_name.trim(), // ✅ display label
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}, [sessions]);



  useEffect(() => {
    getSessions.mutate();
  }, []);

  useEffect(() => {
    if (data) {
      setResultsOpen(true);
    }
  }, [data]);

  useEffect(() => {
    if (data && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [data]);

  const addToLibrary = (files: FileList | null) => {
    if (!files) return;

    const newDocs: LibraryDocument[] = Array.from(files).map((file) => ({
      id: crypto.randomUUID(),
      file,
      uploadedAt: new Date(),
    }));

    setLibraryDocs((prev) => [...prev, ...newDocs]);
  };

  const handleLibraryUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const formData = new FormData();
    formData.append('session', 'CONSUMER_DUTY');
    Array.from(files).forEach((file) => {
      formData.append('files', file); // backend expects files[]
    });

    try {
      const response = await uploadLibraryDocsMutation.mutateAsync(formData);

      // map API response → UI model
      const uploadedDocs: LibraryDocument[] = response.data.map((doc) => ({
        id: doc.id,
        fileName: doc.fileName,
        uploadedAt: new Date(doc.createdAt),
      }));

      setLibraryDocs((prev) => [...prev, ...uploadedDocs]);
    } catch {
      // handled by mutation
    }
  };

  const uploadLibraryDocsMutation = useMutation({
    mutationFn: (payload: FormData) =>
      WPDocumentQA().uploadFiles(payload),

    onSuccess: (response) => {
      enqueueSnackbar('Documents added to library', { variant: 'success' });
    },

    onError: () => {
      enqueueSnackbar('Failed to upload documents', { variant: 'error' });
    },
  });


  const consumerDutyAPI = useMutation({
    mutationFn: (payload: { file: File, session_id: String }) => ComplianceService().consumerDuty(payload),

    onSuccess: ({ data }: { data: ComplianceResponse }) => {
      setData(data);
      enqueueSnackbar('Consumer Duty validated successfully.', { variant: 'success' });
    },

    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error?.[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const canSubmit = useMemo(
    () =>
      !!file &&
      !!selectedTopicId &&
      !loading &&
      !consumerDutyAPI.isPending,
    [file, selectedTopicId, loading, consumerDutyAPI.isPending]
  );


  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setData(null);
    if (!file) {
      setError('Please choose a PDF file first.');
      return;
    }
    try {
      setLoading(true);
      const form = new FormData();
      form.append('file', file);
      await consumerDutyAPI.mutateAsync({
        file,
        session_id: selectedTopicId,
      });
    } catch (err: unknown) {
      setError((err as Error).message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setData(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const heroMode = !data;

  // Derived stats & filtering
  const counts = useMemo(() => {
    const base = {
      total: data?.findings?.length ?? 0,
      fail: 0,
      partial: 0,
      pass: 0,
    };

    if (!data?.findings) return base;

    for (const f of data.findings) {
      if (f.status === 'Fail') base.fail++;
      else if (f.status === 'Partial') base.partial++;
      else base.pass++;
    }

    return base;
  }, [data]);


  const filteredFindings = useMemo(() => {
    if (!data || !Array.isArray(data.findings)) {
      return [] as Finding[];
    }

    const rank: Record<Finding['status'], number> = {
      Fail: 0,
      Partial: 1,
      Pass: 2,
    };

    return data.findings
      .filter((f) => filter === 'ALL' || f.status.toUpperCase() === filter)
      .sort((a, b) => rank[a.status] - rank[b.status]);
  }, [data, filter]);


  return (
    <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.50',
          display: 'flex',
        }}
      >
      {(loading || consumerDutyAPI.isPending) && <WaitScreen fileName={file?.name ?? undefined} />}

      {/* Main */}
      <Container maxWidth="xl" sx={{ px: 2, py: heroMode ? 5 : 4 }}>
        {/* About this tool (3 lines) */}
        <Box sx={{ maxWidth: 900, mx: 'auto', mb: 3 }}>
          {/* Header */}

        <Container maxWidth="xl">
          <Box sx={{ px: 2, py: 3, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Zeta Compliance — Compliance Document Review
            </Typography>
          </Box>
        </Container>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoOutlinedIcon
                  color="primary"
                  sx={{ fontSize: 28, mr: 1 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  About this Tool
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <List dense disablePadding>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <UploadFileIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Upload a suitability report or compliance document and receive structured compliance feedback based on the selected topic."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <AssessmentIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="View an overall compliance indicator and detailed observations with client and adviser feedback."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LibraryBooksIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="View regulatory and policy references to support internal compliance review."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Upload Hero / Docked Card */}
        <Box sx={{ maxWidth: heroMode ? 800 : 'none', mx: 'auto' }}>
          <Box component="form" onSubmit={handleUpload} sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Box
                component="input"
                id="file"
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] || null)}
                sx={{ display: 'none' }}
              />
              <label htmlFor="file">
                <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Compliance topic
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Select the compliance topic this document will be reviewed against.
                  </Typography>

                  <Box sx={{ mt: 1.5 }}>
                    <Select
                      value={selectedTopicId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setSelectedTopicId(id);

                        const topic = complianceTopics.find((t) => t.id === id);
                        setSelectedTopicName(topic?.name ?? '');
                      }}
                      fullWidth
                      size="small"
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select a compliance topic
                      </MenuItem>

                      {complianceTopics.map((topic) => (
                        <MenuItem key={topic.id} value={topic.id}>
                          {topic.name}
                        </MenuItem>
                      ))}
                    </Select>

                  </Box>
                  {selectedTopicName && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      Selected topic: {selectedTopicName}
                    </Typography>
                  )}
                </CardContent>
              </Card>

                <Card
                  sx={{
                    border: 2,
                    borderStyle: 'dashed',
                    borderColor: 'primary.main',
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    textAlign: 'center',
                    p: heroMode ? 7 : 3,
                    '&:hover': {
                      borderColor: 'primary.dark',
                    },
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', fontSize: { xs: '1.125rem', md: '1.5rem' }, fontWeight: 600 }}>
                      Please upload compliance document to review.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Drag & drop or click to choose (PDF only)
                    </Typography>
                    {file && (
                      <Typography variant="body2" sx={{ mt: 1.5 }}>
                        Selected:{' '}
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {file.name}
                        </Typography>
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </label>
            </Box>
            <Box
        component="footer"
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 5,
          px: 2,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  Disclaimer:
                </Typography>{' '}
                Zeta Compliance Agent provides structured compliance feedback and may occasionally produce incomplete or inaccurate outputs.
                . Please review outputs carefully and confirm with your firm's compliance department before taking any action. Zeta Compliance Agent is not responsible for regulatory outcomes;
                Advisors remain accountable for ensuring compliance with FCA requirements.
              </Typography>
            </Alert>
          </Box>
        </Container>
      </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
              <Button
                type="submit"
                disabled={!canSubmit}
                variant="contained"
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.25,
                  minWidth: 140,
                }}
              >
                {loading || consumerDutyAPI.isPending ? 'Reviewing document…' : 'Run review'}
              </Button>
              <Button
                type="button"
                onClick={handleReset}
                variant="outlined"
                size="large"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.25,
                  minWidth: 100,
                }}
              >
                Reset
              </Button>
            </Box>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </Box>

        {/* Results Layout */}
        {data && (
          <ResultsModal
            open={resultsOpen}
            onClose={() => setResultsOpen(false)}
            data={data}
            file={file}
            counts={counts}
            filteredFindings={filteredFindings}
            expandAll={expandAll}
            setExpandAll={setExpandAll}
            filter={filter}
            setFilter={setFilter}
            libraryDocs={libraryDocs}
            selectedTopicName={selectedTopicName}
          />
        )}
      </Container>
      {/* Footer Disclaimer <Box
        sx={{
          width: 750,
          borderLeft: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
          zIndex: 1000,
        }}
      >
        <Box sx={{ p: 2 }}>
          <ComplianceLibraryPanel
            documents={libraryDocs}
            onUpload={handleLibraryUpload}
            onRemove={(id) =>
              setLibraryDocs((prev) => prev.filter((d) => d.id !== id))
            }
          />

        </Box>
      </Box>*/}


    </Box>

  );
}

export default ConsumerDuty;
